import { useEffect, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, TextField, Alert, CircularProgress,
} from "@mui/material";
import procurementService from "api/procurementService";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Kreator poświadczeń klienta OAuth 2.0 (Client Credentials) do publikacji ogłoszeń
 * w Platformie e-Zamówienia (API MO). Poświadczenia (Client ID/Secret + środowisko)
 * pobiera się z Portalu Deweloperskiego e-Zamówienia; sekret nie jest nigdy odczytywany
 * z BE (zwracany jest wyłącznie zamaskowany status).
 */
const ConfigureProcurement = ({ open, onClose }: Props) => {
  const { data: creds, isLoading } = procurementService.useCredentials(open);
  const { mutateAsync: save, isLoading: saving } = procurementService.useSaveCredentials();
  const { mutateAsync: remove, isLoading: removing } = procurementService.useDeleteCredentials();

  const [environment, setEnvironment] = useState("Sandbox");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Wypełnij formularz statusem z BE (bez sekretu — pole sekretu zostaje puste).
  useEffect(() => {
    if (creds?.IsConfigured) {
      setEnvironment(creds.Environment ?? "Sandbox");
      setClientId(creds.ClientId ?? "");
    }
  }, [creds]);

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    if (!clientId.trim()) { setError("Podaj Client ID."); return; }
    if (!clientSecret.trim()) { setError("Podaj Client Secret."); return; }
    try {
      await save({ Environment: environment, ClientId: clientId.trim(), ClientSecret: clientSecret.trim() });
      setClientSecret("");
      setSaved(true);
    } catch {
      setError("Nie udało się zapisać poświadczeń. Spróbuj ponownie.");
    }
  };

  const handleRemove = async () => {
    setError(null);
    try {
      await remove();
      setClientId("");
      setClientSecret("");
      setEnvironment("Sandbox");
      setSaved(false);
    } catch {
      setError("Nie udało się usunąć poświadczeń.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Konfiguracja publikacji ogłoszeń (e-Zamówienia · API MO)
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Alert severity="info" sx={{ fontSize: 12.5 }}>
          Poświadczenia klienta OAuth 2.0 uzyskasz po rejestracji na <b>Portalu Deweloperskim
          e-Zamówienia</b> i wygenerowaniu kluczy. Do testów wybierz środowisko <b>Sandbox</b>.
        </Alert>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <CircularProgress size={22} />
          </div>
        ) : (
          <>
            {creds?.IsConfigured && (
              <Alert severity="success" sx={{ fontSize: 12.5 }}>
                Integracja skonfigurowana — środowisko <b>{creds.Environment}</b>, Client ID{" "}
                <code>{creds.ClientId}</code>, sekret <code>{creds.ClientSecretMask}</code>.
              </Alert>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel>Środowisko</InputLabel>
              <Select label="Środowisko" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                <MenuItem value="Sandbox">Sandbox (testowe)</MenuItem>
                <MenuItem value="Production">Produkcja</MenuItem>
              </Select>
            </FormControl>

            <TextField size="small" label="Client ID" fullWidth value={clientId}
              onChange={(e) => setClientId(e.target.value)} autoComplete="off" />
            <TextField size="small" label="Client Secret" type="password" fullWidth value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)} autoComplete="new-password"
              placeholder={creds?.IsConfigured ? "(pozostaw puste, aby nie zmieniać)" : ""}
              helperText={creds?.IsConfigured ? "Wpisz ponownie tylko przy zmianie sekretu." : undefined} />

            {error && <Alert severity="error" sx={{ fontSize: 12.5 }}>{error}</Alert>}
            {saved && <Alert severity="success" sx={{ fontSize: 12.5 }}>Poświadczenia zapisane.</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <span>
          {creds?.IsConfigured && (
            <Button color="error" onClick={handleRemove} disabled={removing}>
              Usuń poświadczenia
            </Button>
          )}
        </span>
        <span style={{ display: "flex", gap: 8 }}>
          <Button onClick={onClose}>Zamknij</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Zapisywanie…" : "Zapisz"}
          </Button>
        </span>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigureProcurement;
